import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Shield,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Globe,
  Pencil,
  X,
  Check,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface AllowlistEntry {
  id: string;
  cidr: string;
  label: string;
  createdAt: string;
}

interface AllowlistData {
  entries: AllowlistEntry[];
  currentIp: string | null;
  currentIpAllowlisted: boolean | null;
}

function EditRow({
  entry,
  onDone,
}: {
  entry: AllowlistEntry;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [cidr, setCidr] = useState(entry.cidr);
  const [label, setLabel] = useState(entry.label);

  const editMutation = useMutation({
    mutationFn: (body: { cidr: string; label: string }) =>
      apiRequest("PATCH", `/api/platform/ip-allowlist/${entry.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-allowlist"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-check"] });
      toast({ title: "Entry updated", description: "The IP allowlist entry has been updated." });
      onDone();
    },
    onError: (err: any) => {
      toast({ title: "Failed to update", description: err.message, variant: "destructive" });
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cidr.trim()) return;
    editMutation.mutate({ cidr: cidr.trim(), label: label.trim() });
  };

  return (
    <li className="px-6 py-3 bg-muted/40 border-b last:border-b-0" data-testid={`row-ip-entry-edit-${entry.id}`}>
      <form onSubmit={handleSave} className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1 flex-1 min-w-32">
          <Label htmlFor={`edit-cidr-${entry.id}`} className="text-xs">IP / CIDR</Label>
          <Input
            id={`edit-cidr-${entry.id}`}
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            className="h-8 font-mono text-sm"
            required
            data-testid={`input-edit-cidr-${entry.id}`}
          />
        </div>
        <div className="space-y-1 flex-1 min-w-32">
          <Label htmlFor={`edit-label-${entry.id}`} className="text-xs">Label</Label>
          <Input
            id={`edit-label-${entry.id}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="h-8 text-sm"
            data-testid={`input-edit-label-${entry.id}`}
          />
        </div>
        <div className="flex gap-1 pb-0.5">
          <Button
            type="submit"
            size="sm"
            className="h-8 bg-red-600 hover:bg-red-700"
            disabled={editMutation.isPending || !cidr.trim()}
            data-testid={`button-save-ip-${entry.id}`}
          >
            {editMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={onDone}
            data-testid={`button-cancel-edit-ip-${entry.id}`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </form>
    </li>
  );
}

export function IpAllowlistContent() {
  const { toast } = useToast();
  const [newCidr, setNewCidr] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<AllowlistData>({
    queryKey: ["/api/platform/ip-allowlist"],
  });

  const addMutation = useMutation({
    mutationFn: (body: { cidr: string; label: string }) =>
      apiRequest("POST", "/api/platform/ip-allowlist", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-allowlist"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-check"] });
      setNewCidr("");
      setNewLabel("");
      toast({ title: "IP entry added", description: "The IP range has been added to the allowlist." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to add", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/ip-allowlist/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-allowlist"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/ip-check"] });
      toast({ title: "Entry removed", description: "The IP entry has been removed from the allowlist." });
    },
    onError: (err: any) => {
      toast({ title: "Failed to remove", description: err.message, variant: "destructive" });
    },
  });

  const handleAddCurrentIp = () => {
    if (!data?.currentIp) return;
    setNewCidr(data.currentIp);
    setNewLabel("My current IP");
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCidr.trim()) return;
    addMutation.mutate({ cidr: newCidr.trim(), label: newLabel.trim() });
  };

  const entries = data?.entries ?? [];
  const currentIp = data?.currentIp;
  const currentIpAllowlisted = data?.currentIpAllowlisted;
  const allowlistConfigured = entries.length > 0;

  return (
    <>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6" data-testid="page-ip-allowlist">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-600" />
            IP Allowlist
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Trusted IP addresses and CIDR ranges for platform admin login. When configured, logins
            from unlisted IPs require dual-factor verification (SMS + email) before a session is
            granted.
          </p>
        </div>

        {/* Current IP status */}
        {currentIp && (
          <Alert
            className={
              !allowlistConfigured
                ? "border-slate-300"
                : currentIpAllowlisted
                  ? "border-emerald-400"
                  : "border-amber-400"
            }
            data-testid="alert-current-ip-status"
          >
            <Globe className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>
                Your current IP is{" "}
                <code className="font-mono font-semibold">{currentIp}</code>
              </span>
              {!allowlistConfigured ? (
                <Badge variant="outline" className="border-slate-400 text-slate-600">
                  No allowlist configured — all IPs allowed
                </Badge>
              ) : currentIpAllowlisted ? (
                <Badge
                  variant="outline"
                  className="border-emerald-500 text-emerald-700 dark:text-emerald-400"
                  data-testid="badge-ip-allowlisted"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  On allowlist
                </Badge>
              ) : (
                <>
                  <Badge
                    variant="outline"
                    className="border-amber-500 text-amber-700 dark:text-amber-400"
                    data-testid="badge-ip-not-allowlisted"
                  >
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Not on allowlist — dual-MFA required
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    onClick={handleAddCurrentIp}
                    data-testid="button-add-current-ip"
                  >
                    Add my IP
                  </Button>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Add entry form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add IP / CIDR range</CardTitle>
            <CardDescription>
              Enter a single IP address (e.g. <code>1.2.3.4</code>) or a CIDR range (e.g.{" "}
              <code>1.2.3.0/24</code>). IPv4 and IPv6 are both supported.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-1">
                <Label htmlFor="cidr-input">IP / CIDR</Label>
                <Input
                  id="cidr-input"
                  placeholder="203.0.113.0/24"
                  value={newCidr}
                  onChange={(e) => setNewCidr(e.target.value)}
                  data-testid="input-cidr"
                  required
                />
              </div>
              <div className="flex-1 space-y-1">
                <Label htmlFor="label-input">Label (optional)</Label>
                <Input
                  id="label-input"
                  placeholder="Office network"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  data-testid="input-cidr-label"
                />
              </div>
              <div className="flex items-end gap-2">
                {currentIp && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddCurrentIp}
                    data-testid="button-prefill-current-ip"
                    title={`Add current IP (${currentIp})`}
                  >
                    <Globe className="h-4 w-4 mr-1" />
                    Current IP
                  </Button>
                )}
                <Button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={addMutation.isPending || !newCidr.trim()}
                  data-testid="button-add-cidr"
                >
                  {addMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Entries list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Trusted IP ranges</span>
              <Badge variant="secondary" data-testid="badge-entry-count">
                {entries.length} {entries.length === 1 ? "entry" : "entries"}
              </Badge>
            </CardTitle>
            {entries.length === 0 && (
              <CardDescription>
                No entries yet. The allowlist is inactive — all IPs can log in with the normal
                single-MFA flow.
              </CardDescription>
            )}
          </CardHeader>
          {entries.length > 0 && (
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ul className="divide-y">
                  {entries.map((entry) =>
                    editingId === entry.id ? (
                      <EditRow
                        key={entry.id}
                        entry={entry}
                        onDone={() => setEditingId(null)}
                      />
                    ) : (
                      <li
                        key={entry.id}
                        className="flex items-center justify-between px-6 py-3 gap-3"
                        data-testid={`row-ip-entry-${entry.id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-medium">{entry.cidr}</p>
                          {entry.label && (
                            <p className="text-xs text-muted-foreground truncate">{entry.label}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Added {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {currentIp && entry.cidr === currentIp && (
                            <Badge
                              variant="outline"
                              className="text-xs border-emerald-500 text-emerald-600"
                            >
                              Current
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingId(entry.id)}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-edit-ip-${entry.id}`}
                            title="Edit this entry"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteMutation.mutate(entry.id)}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-delete-ip-${entry.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    )
                  )}
                </ul>
              )}
            </CardContent>
          )}
        </Card>

        {/* Explanation */}
        <Card className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4 space-y-2 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">How this works</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                <strong>Empty list</strong> — the allowlist is inactive. All IPs use the normal
                MFA flow (email code for new devices).
              </li>
              <li>
                <strong>At least one entry</strong> — logins from IPs on the list proceed normally.
                Logins from any other IP trigger strict dual-factor verification: a 6-digit code
                sent via <strong>email</strong> AND a second code sent via{" "}
                <strong>SMS</strong>. Both must be entered before a session is created.
              </li>
              <li>
                Dual-MFA requires a verified phone number on your account. If no phone is enrolled,
                logins from unlisted IPs will be <strong>blocked</strong> — add your IP to the
                allowlist while signed in from a trusted location.
              </li>
              <li>
                Codes are single-use and expire in 10 minutes. Up to 5 attempts are allowed, then
                the challenge is invalidated.
              </li>
              <li>
                Failed dual-MFA attempts are recorded in the platform audit log.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default function PlatformIpAllowlist() {
  return <PlatformLayout><IpAllowlistContent /></PlatformLayout>;
}
